import React from 'react';
import { Box, Button, CircularProgress, Dialog } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Contest, Team } from '../../models/types';
import { TeamForm } from './TeamForm';
import { TeamMemberForm } from './TeamMemberForm';
import { TeamMemberImportForm } from './TeamMemberImportForm';
import { ParticipantForm } from './ParticipantForm';
import { ContestForm } from './ContestForm';
import { useContestActions } from './useContestActions';

interface ContestDialogsProps {
  contest: Contest;
  teams: Team[];
  actions: ReturnType<typeof useContestActions>;
}

const Busy = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
    <CircularProgress />
  </Box>
);

// All modal flows of the contest page; each shows a spinner while its request is in flight.
export const ContestDialogs = ({ contest, teams, actions }: ContestDialogsProps) => {
  const { t } = useTranslation('contests');
  const { dialogs, actionLoading } = actions;

  return (
    <>
      <Dialog open={dialogs.team.open} onClose={dialogs.team.close} maxWidth="sm" fullWidth>
        {actionLoading ? <Busy /> : <TeamForm onSave={actions.saveTeam} onCancel={dialogs.team.close} />}
      </Dialog>

      <Dialog open={dialogs.member.open} onClose={dialogs.member.close} maxWidth="sm" fullWidth>
        {actionLoading ? (
          <Busy />
        ) : (
          <TeamMemberForm team={actions.selectedTeam} groupId={contest.groupId} onSave={actions.addTeamMember} onCancel={dialogs.member.close} allTeams={teams} />
        )}
      </Dialog>

      <Dialog open={dialogs.importMembers.open} onClose={dialogs.importMembers.close} maxWidth="sm" fullWidth>
        {dialogs.importMembers.open && (
          <TeamMemberImportForm contestId={contest._id} onImported={actions.onMembersImported} onCancel={dialogs.importMembers.close} />
        )}
      </Dialog>

      <Dialog open={dialogs.participant.open} onClose={dialogs.participant.close} maxWidth="sm" fullWidth>
        {actionLoading ? <Busy /> : <ParticipantForm contestId={contest._id} onSave={actions.addContestParticipant} onCancel={dialogs.participant.close} />}
      </Dialog>

      <Dialog open={dialogs.edit.open} onClose={dialogs.edit.close} maxWidth="sm" fullWidth>
        {actionLoading ? (
          <Busy />
        ) : (
          <ContestForm contest={contest} groupId={contest.groupId} onSave={actions.saveContestEdit} onCancel={dialogs.edit.close} isEdit />
        )}
      </Dialog>

      <Dialog open={dialogs.remove.open} onClose={dialogs.remove.close} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ fontSize: 18, fontWeight: 600 }}>{t('deleteDialog.title')}</Box>
          <Box sx={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.5 }}>
            {t('deleteDialog.message')}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
            <Button variant="outlined" onClick={dialogs.remove.close} disabled={actionLoading}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={actions.confirmDelete}
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {actionLoading ? t('common:actions.deleting') : t('deleteDialog.confirm')}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};
